import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { SESSION_EXPIRED_EVENT } from '../../services/apiClient';

interface AuthContextValue {
  token: string | null;
  role: string | null;
  login: (nextToken: string, nextRole?: string | null) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('shellgame_token'));
  const [role, setRole] = useState<string | null>(() => localStorage.getItem('shellgame_role'));

  const logout = useCallback(() => {
    localStorage.removeItem('shellgame_token');
    localStorage.removeItem('shellgame_role');
    setToken(null);
    setRole(null);
  }, []);

  useEffect(() => {
    // apiClient dispatches this when a request comes back 401 (no/expired token and no renewal
    // happened), which can occur outside of any explicit user action (e.g. an idle tab waking
    // up). Clearing the auth state here lets RequireAuth redirect to /login on the next render.
    window.addEventListener(SESSION_EXPIRED_EVENT, logout);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, logout);
  }, [logout]);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      role,
      login: (nextToken, nextRole) => {
        localStorage.setItem('shellgame_token', nextToken);
        setToken(nextToken);
        if (nextRole) {
          localStorage.setItem('shellgame_role', nextRole);
          setRole(nextRole);
        } else {
          localStorage.removeItem('shellgame_role');
          setRole(null);
        }
      },
      logout,
    }),
    [token, role, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
